import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client/react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  FormFeedback,
  Alert,
  Spinner,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageGuard from '../../components/Common/PageGuard';
import PermissionGate from '../../components/Common/PermissionGate';
import { CREATE_USER, CreateUserVariables } from '../../graphql/mutations/users';
import { useAuth } from '../../contexts/AuthContext';
import { GET_USERS } from '../../graphql/queries/users';

const UsersCreate: React.FC = () => {
  document.title = 'Create User | NGK Tours';
  const navigate = useNavigate();
  const { isSuperAdmin, hasCapability } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const [createUser, { loading }] = useMutation<unknown, CreateUserVariables>(CREATE_USER, {
    refetchQueries: [{ query: GET_USERS }],
    onCompleted: () => {
      navigate('/users-list');
    },
    onError: (err) => {
      setError(err.message || 'Failed to create user');
    },
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: '',
    },
    validationSchema: Yup.object({
      username: Yup.string().required('Username is required'),
      email: Yup.string().email('Invalid email').required('Email is required'),
      password: Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
      firstName: Yup.string(),
      lastName: Yup.string(),
      role: Yup.string().required('Role is required'),
    }),
    onSubmit: (values) => {
      setError(null);
      createUser({
        variables: {
          input: {
            username: values.username,
            email: values.email,
            password: values.password,
            firstName: values.firstName || undefined,
            lastName: values.lastName || undefined,
            roles: values.role ? [values.role] : undefined,
          },
        },
      });
    },
  });

  const availableRoles = isSuperAdmin()
    ? [
        { value: 'super_admin_business', label: 'Super Admin Business' },
        { value: 'admin_business', label: 'Admin Business' },
        { value: 'reservation_business', label: 'Reservation Business' },
        { value: 'account_business', label: 'Account Business' },
        { value: 'operations_business', label: 'Operations Business' },
        { value: 'agent_business', label: 'Agent Business' },
      ]
    : [
        { value: 'admin_business', label: 'Admin Business' },
        { value: 'reservation_business', label: 'Reservation Business' },
        { value: 'account_business', label: 'Account Business' },
        { value: 'operations_business', label: 'Operations Business' },
        { value: 'agent_business', label: 'Agent Business' },
      ];

  return (
    <PageGuard
      requiredCapabilities={['create_users']}
      anyCapability={['business_role_manage', 'business_assign_predefined_roles']}
    >
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Users" breadcrumbItem="Create User" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Create New User</h4>

                  {error && (
                    <Alert color="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Username *</Label>
                          <Input
                            name="username"
                            type="text"
                            placeholder="Enter username"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.username}
                            invalid={!!(validation.touched.username && validation.errors.username)}
                          />
                          {validation.touched.username && validation.errors.username && (
                            <FormFeedback>{validation.errors.username}</FormFeedback>
                          )}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Email *</Label>
                          <Input
                            name="email"
                            type="email"
                            placeholder="Enter email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={!!(validation.touched.email && validation.errors.email)}
                          />
                          {validation.touched.email && validation.errors.email && (
                            <FormFeedback>{validation.errors.email}</FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label>First Name</Label>
                          <Input
                            name="firstName"
                            type="text"
                            placeholder="Enter first name"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.firstName}
                          />
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Last Name</Label>
                          <Input
                            name="lastName"
                            type="text"
                            placeholder="Enter last name"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.lastName}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Password *</Label>
                          <Input
                            name="password"
                            type="password"
                            placeholder="Enter password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={!!(validation.touched.password && validation.errors.password)}
                          />
                          {validation.touched.password && validation.errors.password && (
                            <FormFeedback>{validation.errors.password}</FormFeedback>
                          )}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label>Role *</Label>
                          <Input
                            name="role"
                            type="select"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.role}
                            invalid={!!(validation.touched.role && validation.errors.role)}
                          >
                            <option value="">Select a role</option>
                            {availableRoles.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.role && validation.errors.role && (
                            <FormFeedback>{validation.errors.role}</FormFeedback>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2 justify-content-end mt-4">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => navigate('/users-list')}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" color="primary" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Creating...
                          </>
                        ) : (
                          'Create User'
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </PageGuard>
  );
};

export default UsersCreate;
